import { trackCustomEvent } from './aep_analytics_util.js';

/**
 * Utilities sending messages to Newrelic.
 */

let _pendingRequests = {};

export const GLYPH_MAP_TELEMETRY_EVENT = 'af-glyph-map-telemetry';
export const GLYPH_MAP_OPERATIONAL_ACTION = 'typekit.glyph_map.operational';

export const PAGE_ACTIONS_EXCLUDED_FROM_ADOBE_ANALYTICS = [
  'af-analytics.af-display-in-font.font-preview.load-failed',
  'af-analytics.af-display-in-font.font-preview.load-started',
  'af-analytics.af-glyph-grid.font-inspection-cache-disabled',
  'af-analytics.af-glyph-grid.font-inspection-complete',
  'af-analytics.af-glyph-grid.font-inspection-timeout',
  'af-analytics.af-glyph-grid.font-inspection-unavailable',
  'typekit.font-preview.load-failed',
  'typekit.font-preview.load-started',
  'typekit.glyph_map.fdp_view',
  GLYPH_MAP_OPERATIONAL_ACTION,
  'typekit.intent-recommendations.group.shown',
  'typekit.intent-recommendations.page.time',
  'typekit.intent-recommendations.page.view',
  'typekit.intent-recommendations.results.shown',
  'typekit.susi-light.Account:IMS-Light:SocialProvidersLoaded',
  'typekit.susi-light.Account:IMS-Light:Standard',
  'typekit.top-app-bar.init',
  'typekit.top-app-bar.render'
];

// Includes every kind in @adobe-fonts/site's GlyphMapOperationalRecord union plus host-emitted kinds.
export const SETTLED_OPERATIONAL_KINDS = Object.freeze([
  'activation-origin',
  'activation-timeline',
  'canvas-rendering',
  'category-expansion',
  'coverage-attempt',
  'coverage-divergence',
  'coverage-reuse',
  'deadline',
  'font-cache-summary',
  'font-request',
  'full-font-extraction',
  'inspection',
  'responsiveness',
  'section-import',
  'style-load',
  'viewport-entry',
  'worker-startup'
]);

const SETTLED_OPERATIONAL_KIND_SET = new Set(SETTLED_OPERATIONAL_KINDS);

export const ROUTINE_SUCCESS_OUTCOMES = Object.freeze([
  'activated',
  'active',
  'completed',
  'entered',
  'equal',
  'full',
  'measured',
  'ready',
  'settled',
  'success',
  'usable'
]);

const ROUTINE_SUCCESS_OUTCOME_SET = new Set(ROUTINE_SUCCESS_OUTCOMES);

const UNSETTLED_TELEMETRY_TOKENS = new Set([
  'chunk',
  'frame',
  'hover',
  'pointermove',
  'progress',
  'scroll'
]);

// Initial rollout captures 100% of settled summaries per FONTS-21708 H4. No server code
// sets `window.Typekit.config.glyphMapOperationalSampleRate` yet, so lowering routine-success
// sampling needs that producer or a `setGlyphMapOperationalSampleRate(...)` call; failures,
// timeouts, subset degradations, divergences, and fallbacks are always retained.
export const DEFAULT_GLYPH_MAP_OPERATIONAL_SAMPLE_RATE = 1;

let _glyphMapOperationalSampleRate = DEFAULT_GLYPH_MAP_OPERATIONAL_SAMPLE_RATE;
let _glyphMapOperationalSampler = () => Math.random();
let _glyphMapTrafficCounts = {
  totalForwarded: 0,
  styleLoadCount: 0,
  sampledOutCount: 0,
  droppedUnsettledCount: 0,
  byKind: {}
};

/**
 * Configures the sampling rate and random generator for routine Glyph Map operational events.
 * @param {number} rate - Sampling probability in [0, 1].
 * @param {Function} [sampler] - Random number generator returning a number in [0, 1).
 */
export function setGlyphMapOperationalSampleRate(rate, sampler = () => Math.random()) {
  const numericRate = Number(rate);
  _glyphMapOperationalSampleRate = Number.isFinite(numericRate)
    ? Math.min(1, Math.max(0, numericRate))
    : 1;
  _glyphMapOperationalSampler = sampler;
}

/**
 * Returns the configured sampling rate for routine Glyph Map operational events.
 * @returns {number} Sampling rate in [0, 1].
 */
export function getGlyphMapOperationalSampleRate() {
  return _glyphMapOperationalSampleRate;
}

/**
 * Returns a copy of the current Glyph Map operational traffic counters.
 * @returns {Object} Forwarded, sampled-out, dropped-unsettled, and per-kind counts.
 */
export function getGlyphMapOperationalTrafficCounts() {
  return {
    ..._glyphMapTrafficCounts,
    byKind: { ..._glyphMapTrafficCounts.byKind }
  };
}

/**
 * Resets the Glyph Map operational traffic counters.
 */
export function resetGlyphMapOperationalTrafficCounts() {
  _glyphMapTrafficCounts = {
    totalForwarded: 0,
    styleLoadCount: 0,
    sampledOutCount: 0,
    droppedUnsettledCount: 0,
    byKind: {}
  };
}

function isRoutineSuccessOutcome(detail) {
  if (!detail || typeof detail !== 'object') {
    return false;
  }
  if (
    detail.kind === 'deadline' ||
    detail.fallbackCount > 0 ||
    detail.shadowPolicyExceeded === true ||
    detail.prefetchStatusAtActivation === 'failed-before-activation'
  ) {
    return false;
  }
  return ROUTINE_SUCCESS_OUTCOME_SET.has(detail.outcome);
}

function isSettledOperationalRecord(detail) {
  if (!detail || typeof detail !== 'object') {
    return false;
  }
  const { kind, stage } = detail;
  if (typeof kind !== 'string' || !SETTLED_OPERATIONAL_KIND_SET.has(kind)) {
    return false;
  }
  return !(typeof stage === 'string' && UNSETTLED_TELEMETRY_TOKENS.has(stage));
}

/**
 * Joins array values such as Site responsiveness histogram buckets into comma-separated strings.
 * @param {Object} attributes - Operational record attributes.
 * @returns {Object} Attributes whose values New Relic custom events accept (strings, numbers, booleans, null).
 */
function toNewRelicAttributes(attributes) {
  return Object.fromEntries(Object.entries(attributes).map(([key, value]) =>
    [key, Array.isArray(value) ? value.join(',') : value]
  ));
}

/**
 * Validates, samples, and forwards one settled Glyph Map operational record to New Relic.
 * @param {Object} detail - Settled operational telemetry payload.
 * @returns {boolean} Whether the record was forwarded to New Relic.
 */
export function forwardGlyphMapOperationalRecord(detail) {
  if (!isSettledOperationalRecord(detail)) {
    _glyphMapTrafficCounts.droppedUnsettledCount += 1;
    return false;
  }

  const routineSuccess = isRoutineSuccessOutcome(detail);
  if (
    routineSuccess &&
    _glyphMapOperationalSampleRate < 1 &&
    _glyphMapOperationalSampler() >= _glyphMapOperationalSampleRate
  ) {
    _glyphMapTrafficCounts.sampledOutCount += 1;
    return false;
  }

  const { actionName: _actionName, ...attributes } = detail;
  // A distinct key keeps Site-owned fields such as responsiveness `sampleRate` intact.
  const enrichedAttributes = {
    schemaVersion: 1,
    ...toNewRelicAttributes(attributes),
    ...(_glyphMapOperationalSampleRate < 1 && routineSuccess
      ? { operationalSampleRate: _glyphMapOperationalSampleRate }
      : {})
  };

  // addPageAction drops records until the external New Relic agent loads, so they must not count as forwarded.
  if (typeof window.newrelic === 'undefined' || !window.newrelic) {
    return false;
  }

  _glyphMapTrafficCounts.totalForwarded += 1;
  _glyphMapTrafficCounts.byKind[detail.kind] =
    (_glyphMapTrafficCounts.byKind[detail.kind] || 0) + 1;
  if (detail.kind === 'style-load') {
    _glyphMapTrafficCounts.styleLoadCount += 1;
  }

  addPageAction(GLYPH_MAP_OPERATIONAL_ACTION, enrichedAttributes);
  return true;
}

function handleAfAnalyticsEvent(event) {
  if (!event?.detail) {
    return;
  }
  const actionName = ['af-analytics', event.detail.componentName, event.detail.eventName].join('.');
  const {
    componentName,
    eventName,
    ...attributes
  } = event.detail;

  addPageAction(actionName, attributes);
}

function handleGlyphMapTelemetryEvent(event) {
  // Site records gain glyphMapVisitId and other page attributes from initGlyphMapAnalytics's setCustomAttribute calls.
  forwardGlyphMapOperationalRecord(event?.detail);
}

/**
 * PageActions that aren't registered from an explicit user action aren't sent to
 * Adobe Analytics because they inflate visit counts.
 *
 * From https://experienceleague.adobe.com/en/docs/analytics/components/metrics/visits,
 * "30 minutes of inactivity: Almost all sessions end in this manner. If more than 30 minutes lapse between hits, a new visit begins."
 *
 * Some actions in PAGE_ACTIONS_EXCLUDED_FROM_ADOBE_ANALYTICS occur on every page load.
 * Those count as a "hit" and start counting a subsequent visit. We decided that only explicit user actions should do that.
 *
 * @param {String} actionName
 * @returns {Boolean}
 */
function isExcludedFromAdobeAnalytics(actionName) {
  return PAGE_ACTIONS_EXCLUDED_FROM_ADOBE_ANALYTICS.includes(actionName);
}

/**
  * Report a PageAction event to NewRelic immediately.
  *
  * @param {String} name
  * @param {} attributes
  */
export function addPageAction(name, attributes = {}) {
  // The Newrelic API is loaded through an external JS file, so make sure the
  // API present before continuing.
  if (typeof window.newrelic == 'undefined' || !window.newrelic) {
    return;
  }

  if (!isExcludedFromAdobeAnalytics(name)) {
    trackCustomEvent(name, attributes);
  }
  window.newrelic.addPageAction(name, attributes);
}

/**
  * Report a PageAction event to NewRelic with a de-duplication delay. This
  * waits for the specified number of milliseconds to pass before dispatching
  * the event. If an event with the same name comes in before that, the
  * older event is canceled.
  *
  * @param {String} name
  * @param {} attributes
  * @param {Number} timeoutMs
  */
export function addPageActionWithDelay(name, attributes, timeoutMs) {
  // If there was already a request made for this specific action name, cancel
  // it and make a new request.
  if (_pendingRequests[name]) {
    clearTimeout(_pendingRequests[name]);
  }

  // Wait before sending a newrelic request, so we can cancel the request if
  // another comes in before the timeout has passed.
  _pendingRequests[name] = setTimeout(function() {
    addPageAction(name, attributes);
    delete _pendingRequests[name];
  }, timeoutMs);
}

/**
 * Add listeners for analytics events from adobe-fonts-site and send them to NewRelic
 */
export function init() {
  const configuredSampleRate = window?.Typekit?.config?.glyphMapOperationalSampleRate;
  if (typeof configuredSampleRate === 'number') {
    setGlyphMapOperationalSampleRate(configuredSampleRate);
  }
  window.removeEventListener('af-analytics', handleAfAnalyticsEvent);
  window.addEventListener('af-analytics', handleAfAnalyticsEvent);
  window.removeEventListener(GLYPH_MAP_TELEMETRY_EVENT, handleGlyphMapTelemetryEvent);
  window.addEventListener(GLYPH_MAP_TELEMETRY_EVENT, handleGlyphMapTelemetryEvent);
}

/**
  * Adds a custom attribute to the PageView event via the Browser agent
  *
  * @param {String} name
  * @param {string | number | boolean | null} value
  */
export function setCustomAttribute(name, value) {
  // The Newrelic API is loaded through an external JS file, so make sure the
  // API present before continuing.
  if (!window.newrelic) {
    return;
  }

  window.newrelic.setCustomAttribute(name, value);
}

/**
 * Stringify a click with page name, custom labeling (optional), and event type (optional)
 *
 * @param {String} page
 * @param {Event} event
 * @param {String} label
 * @returns String
 */
export function getNewRelicName(page, event = {}, label = null) {
  const baseName = ['typekit', page];

  if (!event.type) {
    return label ? baseName.concat(label).join('.') : baseName.join('.');
  }

  return label ? baseName.concat(label, event.type).join('.') : baseName.concat(event.type).join('.');
}

/**
 * Get the referrer URL that led the user to the current page
 *
 * @returns String
 */
export function getReferrerHeader() {
  return window.document.referrer;
}

/**
 * Send an analytics event to New Relic with the given action name and attributes.
 * Include the location of the page when event is fired
 * with library-specific attributes if the page is a library detail page.
 * To be used for tracking events that could happen anywhere across the site, like
 * add to web project flow or favorite a font.
 *
 * @param {*} actionName String
 * @param {*} attributes Object
 */
export function sendAnalyticsEventWithPage(actionName, attributes = {}) {
  const location = window.location.pathname.replace('/', '');
  let page = location.split('/')[0];

  if (location.includes('libraries/')) {
    page = 'library-detail-page';

    const library = document.querySelector('af-library-show-page');

    if (library) {
      attributes = {
        ...attributes,
        isLibraryPublic: library.getAttribute('is-library-public')
      };
    }
  }

  addPageAction(`typekit.${page}.${actionName}`, attributes);
}

/**
 * Event handler will listen to all click events that happen on the page
 * for anchor and input elements
 *
 * @param {Object} event - DOM click event.
 * @param {string} page - Current page identifier.
 */
export function handlePageClick(event, page) {
  const targetElement = event.target;

  if (targetElement.matches('a, a *')) {
    handleAnchorClick(event, page);
  } else if (targetElement.matches('input')) {
    handleInputClick(event, page);
  }
}

/**
 * Handles clicking on links. Element's URL and class name are sent to New Relic.
 * @param {Object} event - DOM click event.
 * @param {string} page - Current page identifier.
 */
function handleAnchorClick(event, page) {
  const eventAnchorElement = event.target.closest('A');
  if (eventAnchorElement != null) {
    const elementUrl = eventAnchorElement.href;
    const elementClass = eventAnchorElement.className;
    addPageAction(getNewRelicName(page, {}, 'click'), {elementUrl: elementUrl, elementClass: elementClass});
  }
}

/**
 * Handles input fields. User's input is sent to New Relic.
 * Not applicable for AFS components / input elements within shadow DOM.
 * @param {Object} event - DOM click event.
 * @param {string} page - Current page identifier.
 */
function handleInputClick(event, page) {
  let inputField = event.target;

  if (inputField.type === 'submit') {
    const parentFormElement = event.target.closest('FORM');
    inputField = parentFormElement.querySelector('input[type=text]');
  }
  addPageAction(getNewRelicName(page, {}, 'input'), {inputValue: inputField.value, inputName: inputField.name});
}
